// Thành viên project (SAA-2026-008 UI3.3, ADR-010): cấp / đổi / thu hồi viewer · editor · manager. Canonical ở Platform; thu
// hồi có hiệu lực ngay (scope_version của người bị đổi tăng). Chỉ manager thấy (layout ẩn tab, API trả 403 với người khác).
import { qk, STALE_TIME } from "@secretaa/sdk-react";
import { Button, Card, EmptyState } from "@secretaa/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { api } from "../../app/api.ts";
import { useCurrentWorkspace } from "../../app/session.ts";
import { errorMessage } from "../../shared/errors.ts";
import { RoleNeeded } from "../project/role-hint.tsx";
import { FormError, PageHeader } from "../../shared/form.tsx";

const ROLES = ["viewer", "editor", "manager"] as const;
type Role = (typeof ROLES)[number];

export function ProjectMembersPage() {
  const { t } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const qc = useQueryClient();
  const { projectId = "" } = useParams();
  const grants = useQuery({ queryKey: qk.projectMembers(cur.tenant, projectId), queryFn: () => api.projectMembers.list(cur.workspaceId, projectId), staleTime: STALE_TIME.workspaceMeta });
  const members = useQuery({ queryKey: qk.members(cur.tenant), queryFn: () => api.members.list(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const refresh = () => qc.invalidateQueries({ queryKey: qk.projectMembers(cur.tenant, projectId) });
  const grant = useMutation({ mutationFn: (x: { userId: string; role: Role }) => api.projectMembers.grant(cur.workspaceId, projectId, x.userId, { role: x.role }), onSuccess: async () => (setUserId(""), await refresh()) });
  const revoke = useMutation({ mutationFn: (uid: string) => api.projectMembers.revoke(cur.workspaceId, projectId, uid), onSuccess: refresh });
  if (grants.isPending || members.isPending) return <p>{t("app.loading")}</p>;
  if (grants.isError) return <FormError message={errorMessage(t, grants.error)} />;
  const granted = new Set(grants.data.map((g) => g.userId));
  const candidates = (members.data ?? []).filter((m) => !granted.has(m.userId));
  const selectCls = "h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 text-sm";
  return (
    <>
      <PageHeader title={t("projectMembers.title")} />
      <RoleNeeded projectId={projectId} needs="manager" />
      <FormError message={grant.isError ? errorMessage(t, grant.error) : revoke.isError ? errorMessage(t, revoke.error) : null} />
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
        <label className="flex flex-col gap-1 text-sm font-medium">
          {t("projectMembers.member")}
          <select className={selectCls} value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">{t("projectMembers.pick")}</option>
            {candidates.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName} · {m.email}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium">
          {t("projectMembers.role")}
          <select className={selectCls} value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`projectMembers.roles.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <Button disabled={!userId || grant.isPending} onClick={() => grant.mutate({ userId, role })}>
          {t("projectMembers.grant")}
        </Button>
      </Card>
      {grants.data.length === 0 ? (
        <EmptyState title={t("projectMembers.empty")} />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <caption className="sr-only">{t("projectMembers.title")}</caption>
            <thead className="border-b border-(--saa-border) text-left text-(--saa-fg-muted)">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("projectMembers.member")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("projectMembers.role")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("data.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {grants.data.map((g) => (
                <tr key={g.userId} className="border-b border-(--saa-border) last:border-0">
                  <td className="px-4 py-2">
                    {g.displayName} <span className="text-(--saa-fg-muted)">· {g.email}</span>
                  </td>
                  <td className="px-4 py-2">
                    <select aria-label={t("projectMembers.roleOf", { name: g.displayName })} className={selectCls} value={g.role} onChange={(e) => grant.mutate({ userId: g.userId, role: e.target.value as Role })}>
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {t(`projectMembers.roles.${r}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    <Button variant="secondary" disabled={revoke.isPending} onClick={() => window.confirm(t("projectMembers.revokeConfirm", { name: g.displayName })) && revoke.mutate(g.userId)}>
                      {t("projectMembers.revoke")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
